import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'
import {
  recommendationCardActions,
  recommendationCardAvatar,
  recommendationCardAvatarCheckmark,
  recommendationCardAvatarContainer,
  recommendationCardContainer,
  recommendationCardInfo,
  recommendationCardAvatarCheckmarkIcon,
  recommendationCardContent,
  recommendationCardButton,
  recommendationCardTitle,
  recommendationCardSubtitle,
  recommendationCardDetails,
} from './RecommendationCard.css'
import { ButtonComponent } from '@/components/commons'
import { Icon } from 'public/assets/v2/images/icons'
import {
  IRecommendation,
  ProfileRecommendationType,
} from '@/interfaces/recommendation'
import { inviteContact } from '@/services/connections'
import { UserContext } from '../../context'
import { useMutation } from '@/shared/hooks/useMutation'
import {
  RemoveElementAnimation,
  RemoveElementAnimationContext,
} from '@/components/removeElementAnimation/RemoveElementAnimation'
import { calculateFollowers, cssClass } from '@/shared/utils'
import { RequestConnectionButton } from '@/components/requestConnectionButton/RequestConnectionButton'
import Link from 'next/link'

const RecommendationContext = createContext({} as IRecommendation)

export const RecommendationCard = ({
  recommendation,
  onConnect,
  onDismiss,
}: {
  recommendation: IRecommendation
  onConnect?: () => void
  onDismiss?: () => void
}) => {
  const { user } = useContext(UserContext)
  const useProfilePage =
    recommendation.recommendationType ===
      ProfileRecommendationType.HOT_PROFILE ||
    recommendation.recommendationType ===
      ProfileRecommendationType.COLD_PROFILE ||
    recommendation.recommendationType === ProfileRecommendationType.GPP_PROFILE

  useEffect(() => {
    const userInvitations = user?.invitationsSent || []
    const isInvited = userInvitations.some(
      (invitation) => invitation === recommendation.email
    )
    if (isInvited && onDismiss) {
      onDismiss()
    }
  }, [user, recommendation, onDismiss])

  const recommendedProfileLink = useMemo(() => {
    return recommendation.recommendationType ===
      ProfileRecommendationType.HOT_PROFILE
      ? `/${recommendation.username.toLocaleLowerCase()}`
      : `/cprofile/${recommendation.id}`
  }, [recommendation])

  return (
    <RemoveElementAnimation>
      <div className={recommendationCardContainer}>
        <RecommendationContext.Provider value={recommendation}>
          {recommendation.recommendationType ===
          ProfileRecommendationType.GPP_PROFILE ? (
            <div
              className={cssClass([
                recommendationCardContent,
                useProfilePage ? 'cursor-pointer' : '',
              ])}
            >
              <RecommendationAvatar />
              <div className={recommendationCardInfo}>
                <RecommendationCardTitle />
                <RecommendationCardSubtitle />
                <RecommendationCardDetails />
              </div>
            </div>
          ) : (
            <Link href={recommendedProfileLink} rel="noopener noreferrer">
              <div
                className={cssClass([
                  recommendationCardContent,
                  useProfilePage ? 'cursor-pointer' : '',
                ])}
              >
                <RecommendationAvatar />
                <div className={recommendationCardInfo}>
                  <RecommendationCardTitle />
                  <RecommendationCardSubtitle />
                  <RecommendationCardDetails />
                </div>
              </div>
            </Link>
          )}

          <div className={recommendationCardActions}>
            <RecommendationConnectButton onSuccess={onConnect} />
            <RecommendationInviteButton onSuccess={onConnect} />
            <RecommendationDismissButton onSuccess={onDismiss} />
          </div>
        </RecommendationContext.Provider>
      </div>
    </RemoveElementAnimation>
  )
}

const RecommendationCardTitle = () => {
  const { firstName, lastName } = useContext(RecommendationContext)

  return (
    <span className={recommendationCardTitle}>
      {firstName} {lastName}
    </span>
  )
}

const RecommendationCardSubtitle = () => {
  const { username, company, recommendationType } = useContext(
    RecommendationContext
  )

  const subtitle = useMemo(() => {
    if (recommendationType === ProfileRecommendationType.GPP_PROFILE) {
      return company ?? ' '
    }
    return username
  }, [recommendationType, company, username])

  return <span className={recommendationCardSubtitle}>{subtitle}</span>
}

const RecommendationCardDetails = () => {
  const { totalAudience, recommendationType } = useContext(
    RecommendationContext
  )
  const audienceFormat = calculateFollowers(totalAudience)

  const details = useMemo(() => {
    if (recommendationType === ProfileRecommendationType.GPP_PROFILE) {
      return 'From your contacts'
    }
    return `Total audience ${audienceFormat.value} ${audienceFormat.key}`
  }, [recommendationType, audienceFormat])

  return <span className={recommendationCardDetails}>{details}</span>
}

const RecommendationAvatar = () => {
  const { avatar, username, verified } = useContext(RecommendationContext)
  const profileAvatarSrc = useMemo(() => {
    return avatar || '/assets/avatar.png'
  }, [avatar])
  return (
    <div className={recommendationCardAvatarContainer}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={profileAvatarSrc}
        alt={`${username} avatar`}
        className={recommendationCardAvatar}
        width={48}
        height={48}
      />
      {verified ? (
        <span className={recommendationCardAvatarCheckmark}>
          <Icon.CheckmarkIcon
            className={recommendationCardAvatarCheckmarkIcon}
          />
        </span>
      ) : null}
    </div>
  )
}

const RecommendationConnectButton = ({ onSuccess: _onSuccess }) => {
  const recomendedUser = useContext(RecommendationContext)
  const { triggerAnimation } = useContext(RemoveElementAnimationContext)

  const onSuccess = useCallback(() => {
    triggerAnimation(() => {
      if (_onSuccess) _onSuccess()
    })
  }, [_onSuccess, triggerAnimation])

  if (
    recomendedUser.recommendationType !== ProfileRecommendationType.HOT_PROFILE
  )
    return null

  return (
    <RequestConnectionButton
      onSuccess={onSuccess}
      isInRecommendationCard
      profileFromProps={recomendedUser}
    />
  )
}

const RecommendationInviteButton = ({ onSuccess }) => {
  const recomendedUser = useContext(RecommendationContext)
  const { setUser } = React.useContext(UserContext)
  const { triggerAnimation } = useContext(RemoveElementAnimationContext)

  const [inviteContactRequest, { isLoading }] = useMutation({
    mutateMethod: inviteContact,
  })
  const onClick = useCallback(async () => {
    const result = await inviteContactRequest(recomendedUser)

    if (!result) return

    if ('error' in result) {
      // TODO: Treat error
    } else {
      triggerAnimation(() => {
        setUser(result)
        if (onSuccess) onSuccess()
      })
    }
  }, [
    recomendedUser,
    onSuccess,
    inviteContactRequest,
    setUser,
    triggerAnimation,
  ])

  if (
    recomendedUser.recommendationType === ProfileRecommendationType.HOT_PROFILE
  )
    return null
  return (
    <ButtonComponent
      loading={isLoading}
      className={recommendationCardButton}
      size="xSmall"
      label="Connect"
      onClick={onClick}
    />
  )
}

const RecommendationDismissButton = ({ onSuccess }) => {
  const { triggerAnimation } = useContext(RemoveElementAnimationContext)
  const onClick = useCallback(() => {
    triggerAnimation(onSuccess)
  }, [onSuccess, triggerAnimation])
  return (
    <ButtonComponent
      className={recommendationCardButton}
      variation="secondary"
      size="xSmall"
      onClick={onClick}
      label="Dismiss"
    />
  )
}
